import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Comment01Icon, Book02Icon, Delete01Icon } from 'hugeicons-react';
import { getCompactRelativeTime } from '../../helpers/chatHelper';
import '../../Styles/Partials/ChatListView.css';
import { deleteChat } from '../../services/api';
import { useWindow } from '../../contexts/WindowContext';
import { useUnifiedData } from '../../contexts/AppDataContext';
import { MinimizedChat } from '../../types/chat';

export const ChatListView = () => {
    const { chats, removeChat } = useUnifiedData();
    const [page, setPage] = useState<number>(1);
    const { windowWidth } = useWindow();
    const itemsPerPage = windowWidth > 568 ? (windowWidth > 1268 ? 30 : 30) : 10;
    const sentinelRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const sortedChats = useMemo(() => {
        return [...chats].sort(
            (a: MinimizedChat, b: MinimizedChat) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
        );
    }, [chats]);

    const visibleChats = useMemo(() => {
        return sortedChats.slice(0, page * itemsPerPage);
    }, [sortedChats, page, itemsPerPage]);

    const hasMore = sortedChats.length > visibleChats.length;

    useEffect(() => {
        const currentSentinel = sentinelRef.current;
        const container = containerRef.current;

        if (!currentSentinel || !container) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    setPage((prev) => prev + 1);
                }
            },
            { 
                root: container,
                rootMargin: '100px' 
            }
        );

        observer.observe(currentSentinel);

        return () => {
            observer.unobserve(currentSentinel);
            observer.disconnect();
        };
    }, [hasMore]); // Re-run when hasMore changes

    return (
        <div className="chats-list-container" ref={containerRef}>
            <div className="chats-list-header">
                <h1>Your Conversations</h1>
                <p>Browse and manage your chat history</p>
            </div>

            <ul className="chats-list">
                {visibleChats.map((chat: MinimizedChat) => (
                    <li
                    key={chat.id}
                    className={`recent-chat-item ${chat.spaceId ? 'space-chat' : ''}`}
                    onClick={() => navigate(`/${chat.id}`)}
                  >
                    <div className="chat-preview-icon">
                      {chat.spaceId ? (
                        <Book02Icon size={18} className="space-chat-icon" />
                      ) : (
                        <Comment01Icon size={18} />
                      )}
                    </div>
                    <span className="chat-title" title={chat.title || 'Untitled Chat'}>
                      {windowWidth < 500 && chat.title && chat.title.length > 20
                        ? `${chat.title.substring(0, 30)}...`
                        : (chat.title || 'Untitled Chat')}
                    </span>
                    <span className="chat-date">
                      {getCompactRelativeTime(chat.lastModified)}
                    </span>
                    <button 
                        className="delete-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            deleteChat(chat.id);
                            removeChat(chat.id);
                        }}
                    >
                        <Delete01Icon className="delete-icon" size={16} />
                    </button>
                  </li>
                ))}
            </ul>
            {hasMore && <div ref={sentinelRef} className="sentinel" />}
        </div>
    );
};